<template>
  <v-dialog v-model="dialog" width="800" :persistent="true">
    <template v-slot:activator="{ on }">
      <v-btn color="primary" class="ml-0 mr-0" @click="open" :disabled="disabled" v-if="overwriteOpenModalButtonText">
        {{ overwriteOpenModalButtonText }}
      </v-btn>
      <v-btn color="primary" class="ml-0 mr-0" @click="open" :disabled="disabled" v-else>
        {{ 'app.surveys.add_survey' | trans }}
      </v-btn>
    </template>
    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title v-if="overwriteModalHeader">
          {{ overwriteModalHeader }}
        </v-toolbar-title>
        <v-toolbar-title v-else>
          {{
            (!!surveyId ? 'app.surveys.edit_survey' : 'app.surveys.add_survey') | trans
          }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-container grid-list-md>
          <v-layout wrap align-center>
            <v-flex xs12>
              <v-form ref="form" v-model="valid" lazy-validation>
                <permission-tooltip
                    :permission="!tryToEditWithoutPermissions"
                    :tooltip="{top: true, bottom: false, attach: false}"
                >
                  <v-text-field
                      :disabled="tryToEditWithoutPermissions"
                      :label="'app.name' | trans"
                      clearable
                      v-model="survey.name"
                      :rules="rules.name"
                      full
                  ></v-text-field>
                </permission-tooltip>
              </v-form>
            </v-flex>
          </v-layout>
          <v-divider v-if="!savingAsTemplateMode"></v-divider>
          <v-layout wrap align-center v-if="!savingAsTemplateMode">
            <v-flex xs12>
              <h4>{{ 'app.surveys.questions' | trans }}</h4>
              <draggable v-model="survey.questions" class="mt-4 mb-3" handle=".question_drag_icon">
                <div class="list-group-item"
                     v-for="question in survey.questions"
                     :key="question.order"
                >
                  <v-layout wrap align-center>
                    <v-flex xs10>
                      <v-icon class="question_drag_icon" small>swap_vert</v-icon>
                      <span>{{ question.text }}</span>
                    </v-flex>
                    <v-flex xs2>
                      <v-layout align-center justify-end>
                        <edit-question-modal
                            :question="question.text"
                            @closed="editQuestionModalClosed(question, $event)"
                        ></edit-question-modal>
                        <icon-button
                            @click="copyQuestion(question)"
                            icon="file_copy"
                            color="light-blue"
                            cls="mr-0 ml-0"
                            :tooltip="{top: true, bottom: false, attach: false}"
                            :iconOptions="{small: true}"
                        >
                          {{ 'app.surveys.copy_question' | trans }}
                        </icon-button>
                        <icon-button
                            icon="delete"
                            color="red"
                            cls="mr-0 ml-0"
                            @click="deleteQuestion(question)"
                            :tooltip="{top: true, bottom: false, attach: false}"
                            :iconOptions="{small: true}"
                        >
                          {{ 'app.surveys.delete_question' | trans }}
                        </icon-button>
                      </v-layout>
                    </v-flex>
                  </v-layout>
                </div>
              </draggable>
            </v-flex>
            <v-flex xs8>
              <v-form ref="questionForm" v-model="questionValid" lazy-validation
                      v-on:submit.prevent="addQuestion(question)">
                <v-text-field
                    :label="'app.surveys.question' | trans"
                    clearable
                    v-model="question"
                    :rules="rules.question"
                    full
                ></v-text-field>
              </v-form>
            </v-flex>
            <v-flex xs4>
              <v-btn id="addQuestionButton" color="primary" @click="addQuestion(question)"
                     :disabled="!questionValid || tryToEditWithoutPermissions">
                {{ 'app.surveys.add_question' | trans }}
              </v-btn>
            </v-flex>
            <v-flex xs12>
              <v-messages class="ml-2" v-if="hasEmptyQuestions()"
                          :value="[trans('app.surveys.validation.questions.empty')]"
                          :color="`error`"></v-messages>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
        <permission-tooltip
            :permission="!tryToEditWithoutPermissions"
            :tooltip="{top: true, bottom: false, attach: false}"
        >
          <v-btn color="primary" @click="save" :disabled="!valid || tryToEditWithoutPermissions || hasEmptyQuestions()"
                 :loading="loading">
            <span v-if="surveyId && survey.shared">{{
                         'app.prescriptions.save_as_own' | trans
                       }}</span>
            <span v-else>{{ 'app.save' | trans }}</span>
          </v-btn>
        </permission-tooltip>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {trans} from "../../../app";
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex';

export default {
  components: {
    draggable
  },
  data: function () {
    return {
      dialog: false,
      valid: false,
      questionValid: false,
      loading: false,
      surveyId: null,
      survey: {
        name: '',
        questions: [],
        shared: false,
      },
      question: null,
      shouldValidateEmptyQuestions: false,
    }
  },
  props: {
    currentSurvey: Object,
    disabled: Boolean,
    overwriteOpenModalButtonText: String,
    overwriteModalHeader: String,
    savingAsTemplateMode: {
      Type: Boolean,
      default: false
    }
  },
  watch: {
    currentSurvey: function (survey) {
      if (survey === null) {
        this.surveyId = null;
        this.survey.name = '';
        this.survey.questions = [];
        this.survey.shared = false;
      } else {
        this.surveyId = survey.id;
        this.survey.name = survey.name;
        this.survey.questions = survey.questions;
        this.survey.shared = survey.shared;
      }
      this.question = null;
    }
  },
  computed: {
    ...mapGetters(['customQuestions', 'hasPermission']),
    rules: function () {
      return {
        name: [
          v => !!v || trans('app.surveys.validation.name.required'),
          v => v && v.length >= 1 || trans('app.surveys.validation.name.min', {min: '1'}),
          v => v && v.length <= 50 || trans('app.surveys.validation.name.max', {max: '50'}),
        ],
        question: [
          v => !!v || trans('app.surveys.validation.question.required'),
          v => v && v.length >= 1 || trans('app.surveys.validation.question.min', {min: '1'}),
          v => v && v.length <= 255 || trans('app.surveys.validation.question.max', {max: '255'}),
        ],
      };
    },
    tryToEditWithoutPermissions: function () {
      return !!this.currentSurvey && !this.hasPermission('surveys_edit');
    },
    emptyQuestions: function () {
      return this.survey.questions.length === 0;
    }
  },
  methods: {
    open: function () {
      this.$refs.form.reset();
      if (!this.savingAsTemplateMode) {
        this.$refs.questionForm.reset();
      }
      this.surveyId = null;
      this.dialog = true;
      this.question = null;
      if (this.savingAsTemplateMode) {
        this.survey.questions = this.customQuestions;
      }
    },
    close: function () {
      this.dialog = false;
      this.$refs.form.reset();
      if (!this.savingAsTemplateMode) {
        this.$refs.questionForm.reset();
      }
      this.survey.questions = [];
      this.question = null;
      this.shouldValidateEmptyQuestions = false;
      this.$emit('survey_closed');
    },
    save: async function () {
      this.shouldValidateEmptyQuestions = true;
      if (this.$refs.form.validate() === false || this.emptyQuestions) {
        return;
      }
      this.loading = true;
      if (this.surveyId === null) {
        try {
          await axios.post('/v2/surveys', this.survey);
          this.$toast.success(trans('app.actions.saved', {name: trans('app.survey_accusative')}));
        } catch (e) {
          this.$toast.error(trans('app.save_failed'));
        }
      } else {
        const surveyId = this.surveyId;
        try {
          if (this.survey.shared) {
            await axios.post('/v2/surveys', this.survey);
            this.$toast.success(trans('app.actions.saved', {name: trans('app.survey_accusative')}));
          } else {
            await axios.put(`/v2/surveys/${surveyId}`, this.survey);
            this.$toast.success(trans('app.actions.edited', {name: trans('app.survey_accusative')}));
          }
        } catch (e) {
          this.$toast.error(trans('app.save_failed'));
        }
      }
      this.$emit('survey_stored');
      this.close();
      this.loading = false;
    },
    addQuestion(question) {
      this.shouldValidateEmptyQuestions = true;
      if (question) {
        this.survey.questions.push({
          text: question,
          order: this.survey.questions.length + 1
        });
        this.question = null;
        this.$refs.questionForm.reset();
      }
    },
    copyQuestion(question) {
      this.addQuestion(question.text);
    },
    deleteQuestion(question) {
      this.$dialog.confirm(trans('app.surveys.confirm_delete_question')).then(() => {
        this.shouldValidateEmptyQuestions = true;
        this.survey.questions = this.survey.questions.filter((surveyQuestion) => {
          return surveyQuestion.order !== question.order;
        });
      }).catch(() => {
      });
    },
    hasEmptyQuestions() {
      if (this.shouldValidateEmptyQuestions === false) {
        return false;
      }
      return this.emptyQuestions;
    },
    editQuestionModalClosed(question, payload) {
      question.text = payload.question;
    }
  }
}
</script>
<style scoped>
#addQuestionButton {
  margin-bottom: 30px;
}

.question_drag_icon:hover {
  cursor: grab;
}
</style>